// src/components/Particles.js

import { React, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

// --- TUNABLE PARAMETERS ---
const PARTICLE_COUNT = typeof window !== 'undefined' && (window.innerWidth * window.innerHeight * Math.pow(window.devicePixelRatio || 1, 2)) > 8_000_000 ? 700 : 1000;
const GRAVITY = -20;
const WIND_FORCE = -6.0;
const EXPLOSION_SPEED_MIN = 10;
const EXPLOSION_SPEED_MAX = 20;
const PARTICLE_LIFETIME_MIN = 1.5; // in seconds
const PARTICLE_LIFETIME_MAX = 3.0; // in seconds
const MAX_ANIMATION_TIME = PARTICLE_LIFETIME_MAX + 0.5; // Time before we force a cleanup

const Particles = ({ radius = 5, position = [0, 0, 0] }) => {
  const pointsRef = useRef();
  const materialRef = useRef();
  const animationTimer = useRef(0);

  // Memoize the initial state of each particle so it doesn't get recalculated
  const particles = useMemo(() => {
    const temp = [];

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      // 1. Initial Position: On the surface of the bubble
      const initialPosition = new THREE.Vector3(
        THREE.MathUtils.randFloatSpread(2),
        THREE.MathUtils.randFloatSpread(2),
        THREE.MathUtils.randFloatSpread(2)
      )
        .normalize()
        .multiplyScalar(radius);

      // 2. Initial Velocity: Outward from the center with random speed
      const velocity = initialPosition
        .clone()
        .normalize()
        .multiplyScalar(
          THREE.MathUtils.randFloat(EXPLOSION_SPEED_MIN, EXPLOSION_SPEED_MAX)
        );

      // 3. Lifetime
      const life = THREE.MathUtils.randFloat(
        PARTICLE_LIFETIME_MIN,
        PARTICLE_LIFETIME_MAX
      );
      
      temp.push({
        position: initialPosition,
        velocity,
        life,
        maxLife: life, // Store maxLife for fading calculation
      });
    }
    return temp;
  }, [radius]);

  const dummy = useMemo(() => new THREE.Object3D(), []);

  useFrame((state, delta) => {
    if (!pointsRef.current || !materialRef.current) return;

    // --- Overall animation cleanup ---
    animationTimer.current += delta;
    if (animationTimer.current >= MAX_ANIMATION_TIME) {
      // Removed onExplosionComplete callback - particles stay visible
      return;
    }

    // --- Fade out the entire system over its lifetime ---
    materialRef.current.opacity = Math.max(0, 1.0 - animationTimer.current / MAX_ANIMATION_TIME);


    const gravity = new THREE.Vector3(0, GRAVITY, 0);
    const wind = new THREE.Vector3(WIND_FORCE, 0, 0);

    particles.forEach((particle, i) => {
      // Decrease life
      particle.life -= delta;

      // If particle is dead, hide it and skip
      if (particle.life <= 0) {
        dummy.scale.set(0, 0, 0);
        pointsRef.current.setMatrixAt(i, dummy.matrix);
        return;
      }
      
      // --- Apply Physics ---
      // 1. Update velocity with gravity and wind
      const force = gravity.clone().add(wind);
      particle.velocity.add(force.multiplyScalar(delta));
      
      // 2. Update position with new velocity
      particle.position.add(particle.velocity.clone().multiplyScalar(delta));

      // --- Update the instance ---
      dummy.position.copy(particle.position);
      
      // Optional: make particles shrink as they die
      const scale = Math.max(0, particle.life / particle.maxLife);
      dummy.scale.set(scale, scale, scale);

      dummy.updateMatrix();
      pointsRef.current.setMatrixAt(i, dummy.matrix);
    });
    
    pointsRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <group position={position}>
      <instancedMesh ref={pointsRef} args={[null, null, PARTICLE_COUNT]}>
        {/* A small sphere is fine, but a plane facing the camera is often more performant */}
        <sphereGeometry args={[0.05, 6, 6]} />
        <meshStandardMaterial
          ref={materialRef}
          color="#FFFFFF"
          transparent={true}
          // Make them look more like water droplets
          envMapIntensity={0.5}
          opacity={0.8}
        />
      </instancedMesh>
    </group>
  );
};

export default Particles;